/**
 * Portal Component
 *
 * Renders children into a DOM node that exists outside the parent component's DOM hierarchy.
 * Useful for modals, dropdowns, tooltips, and other overlays.
 */

import { useEffect, useState, type ReactNode, type ReactPortal, type RefObject } from 'react';
import { createPortal } from 'react-dom';

import { logger } from '../../utils/logger';

export interface PortalProps {
  /** Content to render in portal */
  children: ReactNode;
  /** Optional container ref to render into (defaults to document.body) */
  containerRef?: RefObject<HTMLElement | null>;
  /** Optional container selector (e.g., '#portal-root') */
  container?: string;
}

export const Portal = ({ children, containerRef, container }: PortalProps): ReactPortal | null => {
  const [mounted, setMounted] = useState(false);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setMounted(true);

    // Determine container
    let targetContainer: HTMLElement | null = null;

    if (containerRef?.current) {
      targetContainer = containerRef.current;
    } else if (container) {
      targetContainer = document.querySelector(container);
    } else {
      // Try to get document.body, but handle cases where it might not exist
      try {
        targetContainer = document.body;
      } catch {
        // In some test environments or SSR, document.body might not be available
        targetContainer = document.documentElement;
      }
    }

    // Ensure we have a valid container
    if (!targetContainer) {
      logger.warn('Portal: Could not find a valid container element');
      targetContainer = document.documentElement;
    }

    setPortalContainer(targetContainer);

    return () => {
      setMounted(false);
    };
  }, [containerRef, container]);

  // Don't render during SSR, but don't block rendering if container is being determined
  if (!mounted) {
    return null;
  }

  // Render even if portalContainer is null, and let createPortal handle it
  // This prevents the dropdown from disappearing when container is being resolved
  const containerElement = portalContainer || document.body;
  return createPortal(children, containerElement);
};

Portal.displayName = 'Portal';
